import { frameId } from "../assets/manifest.ts";
import { asArray, asNumber, asRecord, asString } from "../document/validate.ts";
import type { MapDocument } from "../document/types.ts";
import { lightPoint, type LightWhen } from "../effects/frame-light.ts";
import type { CompileContext } from "./context.ts";

/**
 * The energy sources of a server chunk (ADR-141's amendment of 2026-11-01, decision 6): every placed copy of a frame whose
 * light has its source switch on, at the light's anchor, so the map server can seed each source's live state. Only server
 * chunks carry them; a chunk with none carries no field, so a map with no source compiles byte for byte as before.
 */

/**
 * The most sources one chunk may list.
 */
export const MAX_CHUNK_SOURCES = 256;

/**
 * One source of a chunk: the placed prop's id (its stable key), the source's kind, its anchor in whole world pixels, and its
 * light's radius, strength and `when`.
 */
export interface ChunkSource {
  readonly id: string;
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly strength: number;
  readonly when: LightWhen;
}

/**
 * A map's sources by the chunk that holds each prop, keyed `cy * columns + cx`.
 *
 * @param map The map.
 * @param context The compile context; its `lights` and `frameSizes` are read.
 * @param tileSize Tile size in pixels.
 * @param chunkSize Chunk size in tiles.
 * @param columns The map's chunk columns.
 * @returns The table, empty when the map has no source.
 * @throws Error when a chunk would hold more than `MAX_CHUNK_SOURCES`.
 */
export function chunkSourceTable(map: MapDocument, context: CompileContext, tileSize: number, chunkSize: number, columns: number): ReadonlyMap<number, readonly ChunkSource[]> {
  const table = new Map<number, ChunkSource[]>();
  const lights = context.lights;
  if (lights === undefined) {
    return table;
  }
  const point = { x: 0, y: 0 };
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const id = frameId(prop.frame.tileset, null, prop.frame.tile);
      const light = lights[id];
      if (light?.source === undefined) {
        continue;
      }
      const size = context.frameSizes?.[id] ?? { w: tileSize, h: tileSize };
      lightPoint(light, prop, size, tileSize, point);
      const key = Math.floor(Math.floor(prop.y) / chunkSize) * columns + Math.floor(Math.floor(prop.x) / chunkSize);
      const list = table.get(key) ?? [];
      list.push({ id: prop.id, kind: light.source, x: Math.round(point.x), y: Math.round(point.y), radius: light.radius, strength: light.strength, when: light.when });
      table.set(key, list);
      if (list.length > MAX_CHUNK_SOURCES) {
        throw new Error(`a chunk of map ${map.id} holds ${list.length} energy sources, at most ${MAX_CHUNK_SOURCES}`);
      }
    }
  }
  return table;
}

/**
 * Reads a chunk's sources.
 *
 * @param value The `sources` value.
 * @returns The sources.
 * @throws Error naming the path of the first fault.
 */
export function readChunkSources(value: unknown): ChunkSource[] {
  const items = asArray(value, "sources");
  if (items.length > MAX_CHUNK_SOURCES) {
    throw new Error(`sources has ${items.length} entries, at most ${MAX_CHUNK_SOURCES}`);
  }
  return items.map((item, i) => {
    const path = `sources[${i}]`;
    const record = asRecord(item, path);
    const id = asString(record["id"], `${path}.id`);
    const kind = asString(record["kind"], `${path}.kind`);
    if (id === "") throw new Error(`${path}.id is blank`);
    if (!/^[a-z][a-z0-9-]{0,23}$/.test(kind)) throw new Error(`${path}.kind must be a lower-case name`);
    const whole = (key: string): number => {
      const read = asNumber(record[key], `${path}.${key}`);
      if (!Number.isInteger(read)) throw new Error(`${path}.${key} must be a whole number`);
      return read;
    };
    const radius = whole("radius");
    if (radius <= 0) throw new Error(`${path}.radius must be above 0`);
    const strength = asNumber(record["strength"], `${path}.strength`);
    const when = record["when"];
    if (when !== "always" && when !== "dusk") throw new Error(`${path}.when must be always or dusk`);
    return { id, kind, x: whole("x"), y: whole("y"), radius, strength, when };
  });
}
